<template>
  <div class="ur-settings ur-admins">

    <!-- Header（返回鍵 + 標題 + 新增管理者，設計稿）-->
    <div class="ur-settings-header ur-settings-topbar">
      <button class="ur-settings-back" :title="$t('URBANRENEWAL_ADMIN.ADMINS.BACK')" @click="goBack">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M5.21917 12.6666L11.4144 18.8619L10.4716 19.8047L3.33356 12.6666L3.27637 12.6666L3.27637 12.6094L2.66689 12L3.27637 11.3905L3.27637 11.3333L3.33356 11.3333L10.4716 4.19521L11.4144 5.13802L5.21918 11.3333L19.2764 11.3333L19.2764 12.6666L5.21917 12.6666Z" fill="currentColor"></path>
        </svg>
      </button>
      <span class="ur-settings-title">{{ $t('URBANRENEWAL_ADMIN.ADMINS.TITLE') }}</span>
      <button class="ur-admins-add-btn" :disabled="editKey !== null" @click="addAdmin">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
             stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        {{ $t('URBANRENEWAL_ADMIN.ADMINS.ADD') }}
      </button>
    </div>

    <!-- Error message -->
    <div v-if="errorMsg" class="ur-settings-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-settings-error-close" @click="errorMsg = ''">&times;</button>
    </div>

    <!-- Success toast -->
    <div v-if="successMsg" class="ur-admins-success">
      <span>{{ successMsg }}</span>
      <button class="ur-admins-success-close" @click="successMsg = ''">&times;</button>
    </div>

    <!-- 管理者卡片列表（設計稿）-->
    <div class="ur-admins-list">
      <!-- Empty state -->
      <div v-if="!loading && rows.length === 0" class="ur-admins-empty">
        {{ $t('URBANRENEWAL_ADMIN.ADMINS.NO_DATA') }}
      </div>

      <div
        v-for="row in rows"
        :key="row._key"
        class="ur-admin-card"
        :class="{ 'is-inactive': !row.is_active && !row._isNew && editKey !== row._key }"
      >
        <!-- 編輯模式 -->
        <template v-if="editKey === row._key">
          <div class="ur-admin-edit-grid">
            <div class="ur-admin-edit-field">
              <label class="ur-admin-edit-label">{{ $t('URBANRENEWAL_ADMIN.ADMINS.COL_LAST_NAME') }}</label>
              <input class="ur-admin-edit-input" v-model="draft.last_name" :placeholder="$t('URBANRENEWAL_ADMIN.ADMINS.PH_LAST')" />
            </div>
            <div class="ur-admin-edit-field">
              <label class="ur-admin-edit-label">{{ $t('URBANRENEWAL_ADMIN.ADMINS.COL_FIRST_NAME') }}</label>
              <input class="ur-admin-edit-input" v-model="draft.first_name" :placeholder="$t('URBANRENEWAL_ADMIN.ADMINS.PH_FIRST')" />
            </div>
            <div class="ur-admin-edit-field ur-admin-edit-field-wide">
              <label class="ur-admin-edit-label">{{ $t('URBANRENEWAL_ADMIN.ADMINS.ACCOUNT') }}</label>
              <input class="ur-admin-edit-input" type="email" v-model="draft.email" :placeholder="$t('URBANRENEWAL_ADMIN.ADMINS.PH_EMAIL')" />
            </div>
          </div>
          <div class="ur-admin-edit-actions">
            <button class="ur-admin-cancel-btn" @click="cancelEdit(row)">{{ $t('URBANRENEWAL_ADMIN.ADMINS.CANCEL') }}</button>
            <button class="ur-admin-save-btn" :disabled="!canSaveDraft" @click="saveEdit(row)">{{ $t('URBANRENEWAL_ADMIN.ADMINS.SAVE') }}</button>
          </div>
        </template>

        <!-- 停用中：頭像+資料 + 重啟 -->
        <template v-else-if="!row.is_active">
          <div class="ur-admin-card-row">
            <div class="ur-admin-avatar">{{ initials(row) }}</div>
            <div class="ur-admin-card-info">
              <div class="ur-admin-card-name">
                {{ fullName(row) }}
                <span class="ur-admin-inactive-tag">{{ $t('URBANRENEWAL_ADMIN.ADMINS.STATUS_INACTIVE') }}</span>
              </div>
              <div class="ur-admin-card-email">{{ row.email || '—' }}</div>
            </div>
            <div class="ur-admin-card-icons">
              <button class="ur-admin-reactivate-btn" @click="reactivate(row)">
                {{ $t('URBANRENEWAL_ADMIN.ADMINS.REACTIVATE') }}
              </button>
            </div>
          </div>
        </template>

        <!-- 顯示模式（啟用）-->
        <template v-else>
          <div class="ur-admin-card-row">
            <div class="ur-admin-avatar">{{ initials(row) }}</div>
            <div class="ur-admin-card-info">
              <div class="ur-admin-card-name">{{ fullName(row) }}</div>
              <div class="ur-admin-card-email">{{ row.email || '—' }}</div>
            </div>
            <div class="ur-admin-card-icons">
              <button class="ur-admin-icon-btn is-edit" :title="$t('URBANRENEWAL_ADMIN.ADMINS.EDIT')" @click="startEdit(row)">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
              </button>
              <button v-if="!isSelf(row)" class="ur-admin-icon-btn is-del" :title="$t('URBANRENEWAL_ADMIN.ADMINS.DELETE')" @click="askDelete(row)">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 品牌 Logo 卡（設計稿無此區 → 移至最下方）-->
    <div class="ur-branding-card" style="margin-top:24px;">
      <div class="ur-branding-header">
        <span class="ur-branding-title">{{ $t('URBANRENEWAL_ADMIN.BRANDING_TITLE') }}</span>
      </div>
      <div class="ur-branding-body">
        <div class="ur-branding-preview">
          <img :src="logoUrl" class="ur-branding-logo-preview" alt="logo" />
        </div>
        <div class="ur-branding-hint">{{ $t('URBANRENEWAL_ADMIN.LOGO_HINT') }}</div>
        <div class="ur-branding-actions">
          <button class="ur-settings-add-btn" :disabled="logoUploading" @click="triggerLogo">
            {{ $t('URBANRENEWAL_ADMIN.UPLOAD_LOGO') }}
          </button>
          <button class="ur-admin-action-btn is-disable" style="margin-left:8px;" @click="removeLogo">
            {{ $t('URBANRENEWAL_ADMIN.REMOVE_LOGO') }}
          </button>
        </div>
        <input
          type="file"
          ref="logoInput"
          accept="image/*"
          style="display:none"
          :key="logoFileKey"
          @change="onLogoChange"
        />
      </div>
    </div>

    <!-- AI 客服設定（UpChat webchat；url + token per-tenant，測試/正式可換）-->
    <div class="ur-branding-card" style="margin-top:24px;">
      <div class="ur-branding-header">
        <span class="ur-branding-title">{{ $t('URBANRENEWAL_ADMIN.AI_SUPPORT.TITLE') }}</span>
      </div>
      <div class="ur-branding-body">
        <label class="ur-ai-enable">
          <input type="checkbox" v-model="aiSupport.enabled" />
          {{ $t('URBANRENEWAL_ADMIN.AI_SUPPORT.ENABLE') }}
        </label>
        <div class="ur-ai-field">
          <label class="ur-admin-edit-label">{{ $t('URBANRENEWAL_ADMIN.AI_SUPPORT.URL') }}</label>
          <input class="ur-admin-edit-input" type="text" v-model="aiSupport.url" :placeholder="$t('URBANRENEWAL_ADMIN.AI_SUPPORT.URL_PH')" />
        </div>
        <div class="ur-ai-field">
          <label class="ur-admin-edit-label">{{ $t('URBANRENEWAL_ADMIN.AI_SUPPORT.TOKEN') }}</label>
          <input class="ur-admin-edit-input" type="text" v-model="aiSupport.token" :placeholder="$t('URBANRENEWAL_ADMIN.AI_SUPPORT.TOKEN_PH')" />
        </div>
        <div class="ur-branding-actions">
          <button class="ur-admin-save-btn" :disabled="aiSaving" @click="saveAiSupportConfig">
            {{ $t('URBANRENEWAL_ADMIN.AI_SUPPORT.SAVE') }}
          </button>
          <span v-if="aiStatus" class="ur-ai-status" :class="{ 'is-err': !aiStatusOk }">
            {{ aiStatusOk ? '✓ ' : '' }}{{ aiStatus }}
          </span>
        </div>
      </div>
    </div>

    <!-- 停用確認 Dialog -->
    <v-dialog v-model="deleteDialog.visible" max-width="420" @click:outside="deleteDialog.visible = false">
      <v-card>
        <div class="ur-settings-dialog-body">
          <p class="ur-settings-dialog-msg">
            {{ deleteDialog.admin
              ? $t('URBANRENEWAL_ADMIN.ADMINS.DELETE_CONFIRM', {
                  name: deleteDialog.admin.last_name + deleteDialog.admin.first_name || deleteDialog.admin.email
                })
              : '' }}
          </p>
        </div>
        <div class="ur-settings-dialog-actions">
          <button class="ur-settings-dialog-cancel-btn" @click="deleteDialog.visible = false">
            {{ $t('URBANRENEWAL_ADMIN.ADMINS.CANCEL') }}
          </button>
          <button class="ur-settings-dialog-confirm-btn" @click="confirmDelete">
            {{ $t('URBANRENEWAL_ADMIN.ADMINS.DELETE') }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';

export default {
  name: 'UrbanRenewalAdminAdminSettings',

  data() {
    return {
      rows: [],
      loading: false,
      errorMsg: '',
      successMsg: '',
      keySeq: 0,
      editKey: null,                                  // 目前編輯中的卡片 _key（null = 無）
      draft: { email: '', first_name: '', last_name: '' },
      deleteDialog: {
        visible: false,
        admin: null,
      },
      logoUrl: require('@/assets/images/UPGPT.png'),
      logoUploading: false,
      logoFileKey: 0,
      aiSupport: { enabled: false, url: '', token: '' },   // AI 客服（UpChat webchat）設定
      aiSaving: false,
      aiStatus: '',        // 儲存後就地回饋（按鈕旁）
      aiStatusOk: true,
      aiStatusTimer: null,
    };
  },

  computed: {
    canSaveDraft() {
      const e = this.draft.email;
      return !!(e && e.trim() && e.indexOf('@') !== -1);
    },
  },

  mounted() {
    this.loadRows();
    this.fetchLogo();
    this.loadAiSupport();
  },

  methods: {
    async loadAiSupport() {
      try {
        const res = await UrbanRenewalDataService.getAiSupport();
        const d = res.data || {};
        this.aiSupport = { enabled: !!d.enabled, url: d.url || '', token: d.token || '' };
      } catch (_) {
        // 保留預設（停用/空）
      }
    },

    async saveAiSupportConfig() {
      this.aiSaving = true;
      this.aiStatus = '';
      if (this.aiStatusTimer) { clearTimeout(this.aiStatusTimer); this.aiStatusTimer = null; }
      try {
        await UrbanRenewalDataService.saveAiSupport({
          enabled: this.aiSupport.enabled,
          url: (this.aiSupport.url || '').trim(),
          token: (this.aiSupport.token || '').trim(),
        });
        // 就地回饋（按鈕旁），成功 2.5 秒後自動消失
        this.aiStatusOk = true;
        this.aiStatus = this.$t('URBANRENEWAL_ADMIN.AI_SUPPORT.SAVED');
        this.aiStatusTimer = setTimeout(() => { this.aiStatus = ''; }, 2500);
      } catch (_) {
        this.aiStatusOk = false;
        this.aiStatus = this.$t('URBANRENEWAL_ADMIN.AI_SUPPORT.SAVE_FAILED');
      } finally {
        this.aiSaving = false;
      }
    },

    async fetchLogo() {
      try {
        const res = await UrbanRenewalDataService.getLogo();
        this.logoUrl = res.data === '' ? require('@/assets/images/UPGPT.png') : res.data;
      } catch (_) {
        // 保留預設圖
      }
    },

    triggerLogo() {
      this.$refs.logoInput.click();
    },

    async onLogoChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      // 前置驗證:僅接受圖片檔,避免上傳非圖片後變破圖(後端亦有把關)
      if (!file.type || !file.type.startsWith('image/')) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.LOGO_INVALID_TYPE');
        this.logoFileKey++;
        return;
      }
      this.logoUploading = true;
      try {
        const formData = new FormData();
        formData.append('logo', file);
        const res = await UrbanRenewalDataService.uploadLogo(formData);
        this.logoUrl = res.data;
        this.$store.commit('urbanRenewalAdmin/SET_LOGO_URL', res.data);
        this.successMsg = this.$t('URBANRENEWAL_ADMIN.LOGO_UPLOADED');
      } catch (err) {
        this.errorMsg =
          (err.response && err.response.data && err.response.data.detail) ||
          this.$t('URBANRENEWAL_ADMIN.LOGO_UPLOAD_FAILED');
      } finally {
        this.logoUploading = false;
        this.logoFileKey++;
      }
    },

    async removeLogo() {
      try {
        await UrbanRenewalDataService.deleteLogo();
        this.logoUrl = require('@/assets/images/UPGPT.png');
        this.$store.commit('urbanRenewalAdmin/SET_LOGO_URL', '');
        this.successMsg = this.$t('URBANRENEWAL_ADMIN.LOGO_REMOVED');
      } catch (_) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.LOGO_UPLOAD_FAILED');
      }
    },

    newKey() {
      this.keySeq += 1;
      return this.keySeq;
    },

    goBack() {
      this.$router.back();
    },

    initials(row) {
      const a = (row.last_name || '')[0] || '';
      const b = (row.first_name || '')[0] || '';
      return (a + b) || '?';
    },

    fullName(row) {
      return [row.last_name, row.first_name].filter(Boolean).join(' ') || '—';
    },

    startEdit(row) {
      this.errorMsg = '';
      this.editKey = row._key;
      this.draft = {
        email: row.email,
        first_name: row.first_name,
        last_name: row.last_name,
      };
    },

    cancelEdit(row) {
      // 新增中取消 → 移除該暫存卡片
      if (row._isNew) {
        this.rows = this.rows.filter((r) => r._key !== row._key);
      }
      this.editKey = null;
      this.draft = { email: '', first_name: '', last_name: '' };
    },

    async saveEdit(row) {
      if (!this.canSaveDraft) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.ADMINS.EMAIL_REQUIRED');
        return;
      }
      this.errorMsg = '';
      const payload = {
        email: this.draft.email.trim(),
        first_name: this.draft.first_name,
        last_name: this.draft.last_name,
      };
      try {
        if (row._isNew) {
          await UrbanRenewalDataService.createAdmin(payload);
          this.successMsg = this.$t('URBANRENEWAL_ADMIN.ADMINS.CREATED');
        } else {
          await UrbanRenewalDataService.updateAdmin(row.id, payload);
          this.successMsg = this.$t('URBANRENEWAL_ADMIN.ADMINS.SAVED');
        }
        this.editKey = null;
        this.draft = { email: '', first_name: '', last_name: '' };
        await this.loadRows();
      } catch (e) {
        if (e.response && e.response.status === 400 && e.response.data && e.response.data.detail) {
          this.errorMsg = e.response.data.detail;
        } else {
          this.errorMsg = this.$t('URBANRENEWAL_ADMIN.ADMINS.SAVE_FAILED');
        }
      }
    },

    async loadRows() {
      this.loading = true;
      try {
        const res = await UrbanRenewalDataService.getAdmins();
        const list = res.data || [];
        this.rows = list.map(u => ({
          _key: this.newKey(),
          id: u.id,
          email: u.email,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          is_active: u.is_active,
          last_login: u.last_login || null,
          _isNew: false,
        }));
      } catch (_) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.ADMINS.LOAD_FAILED');
      } finally {
        this.loading = false;
      }
    },

    addAdmin() {
      if (this.editKey !== null) return;            // 一次只編輯一張卡
      const k = this.newKey();
      this.rows.unshift({
        _key: k,
        id: null,
        email: '',
        first_name: '',
        last_name: '',
        is_active: true,
        last_login: null,
        _isNew: true,
      });
      this.editKey = k;
      this.draft = { email: '', first_name: '', last_name: '' };
    },

    askDelete(row) {
      this.deleteDialog = { visible: true, admin: row };
    },

    // 是否為目前登入中的管理者(用來隱藏「停用自己」)
    isSelf(row) {
      try {
        const u = JSON.parse(window.localStorage.getItem('user') || '{}');
        return row.id != null && String(row.id) === String(u.userid);
      } catch (_) {
        return false;
      }
    },

    async confirmDelete() {
      if (!this.deleteDialog.admin) return;
      try {
        await UrbanRenewalDataService.deactivateAdmin(this.deleteDialog.admin.id);
        this.deleteDialog.visible = false;
        await this.loadRows();
      } catch (e) {
        this.deleteDialog.visible = false;
        this.errorMsg =
          (e.response && e.response.data && e.response.data.detail) ||
          this.$t('URBANRENEWAL_ADMIN.ADMINS.SAVE_FAILED');
      }
    },

    async reactivate(row) {
      try {
        await UrbanRenewalDataService.activateAdmin(row.id);
        this.successMsg = this.$t('URBANRENEWAL_ADMIN.ADMINS.REACTIVATED');
        await this.loadRows();
      } catch (_) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.ADMINS.SAVE_FAILED');
      }
    },
  },
};
</script>
